import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  type IntegrationData,
  type IntegrationUpdatePayload,
  integrationsService,
} from '@/services/integrations'
import { useAuthStore } from '@/stores/auth'

export const integrationKeys = {
  all: (orgId?: string) => ['integrations', orgId] as const,
  list: (orgId?: string) => [...integrationKeys.all(orgId), 'list'] as const,
  detail: (type: string, orgId?: string) =>
    [...integrationKeys.all(orgId), 'detail', type] as const,
}

export function useIntegrations() {
  const { organization } = useAuthStore()

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: integrationKeys.list(organization?.id),
    queryFn: () => integrationsService.getIntegrations(),
    enabled: !!organization?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  })

  const integrations: IntegrationData[] = data ?? []

  const active = integrations.filter((i) => i.connected)
  const inactive = integrations.filter((i) => !i.connected)

  const isConnected = (type: string): boolean =>
    integrations.some((i) => i.integrationType === type && i.connected)

  return {
    integrations,
    active,
    inactive,
    isLoading,
    isFetching,
    error,
    refetch,
    isConnected,
  }
}

function optimisticMutationCallbacks<TVariables>(
  queryClient: QueryClient,
  listKey: readonly unknown[],
  allKey: readonly unknown[],
  updater: (old: IntegrationData[], vars: TVariables) => IntegrationData[]
) {
  return {
    onMutate: async (vars: TVariables) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      const previousIntegrations =
        queryClient.getQueryData<IntegrationData[]>(listKey)
      queryClient.setQueryData<IntegrationData[]>(listKey, (old) =>
        old ? updater(old, vars) : old
      )
      return { previousIntegrations }
    },
    onError: (
      _err: unknown,
      _vars: TVariables,
      context?: { previousIntegrations?: IntegrationData[] }
    ) => {
      if (context?.previousIntegrations) {
        queryClient.setQueryData(listKey, context.previousIntegrations)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: allKey })
    },
  }
}

function upsertIntegration(
  list: IntegrationData[],
  type: string,
  config: IntegrationUpdatePayload['config'],
  enabled: boolean
): IntegrationData[] {
  const exists = list.some((i) => i.integrationType === type)
  if (exists) {
    return list.map((i) =>
      i.integrationType === type
        ? { ...i, connected: true, enabled, config }
        : i
    )
  }
  return [
    ...list,
    {
      integrationType: type,
      connected: true,
      enabled,
      config,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ]
}

export function useIntegrationMutations() {
  const queryClient = useQueryClient()
  const { organization } = useAuthStore()

  const listKey = integrationKeys.list(organization?.id)
  const allKey = integrationKeys.all(organization?.id)

  const connectMutation = useMutation({
    mutationFn: ({
      type,
      payload,
    }: {
      type: string
      payload: IntegrationUpdatePayload
    }) => integrationsService.updateIntegration(type, payload),
    ...optimisticMutationCallbacks(
      queryClient,
      listKey,
      allKey,
      (old, { type, payload }) =>
        upsertIntegration(old, type, payload.config, payload.enabled ?? true)
    ),
  })

  const disconnectMutation = useMutation({
    mutationFn: (type: string) => integrationsService.deleteIntegration(type),
    ...optimisticMutationCallbacks(queryClient, listKey, allKey, (old, type) =>
      old.map((i) =>
        i.integrationType === type
          ? { ...i, connected: false, enabled: false, config: {} }
          : i
      )
    ),
  })

  const requestIntegrationMutation = useMutation({
    mutationFn: ({
      integrationName,
      description,
    }: {
      integrationName: string
      description: string
    }) => integrationsService.requestIntegration(integrationName, description),
  })

  const requestProfilePropertiesContactMutation = useMutation({
    mutationFn: () => integrationsService.requestProfilePropertiesContact(),
  })

  const testConnectionMutation = useMutation({
    mutationFn: ({
      type,
      privateApiKey,
    }: {
      type: string
      privateApiKey: string
    }) => integrationsService.testConnection(type, privateApiKey),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: allKey })
    },
  })

  const testClickpointConnectionMutation = useMutation({
    mutationFn: (apiKey: string) =>
      integrationsService.testClickpointConnection(apiKey),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: allKey })
    },
  })

  return {
    connect: connectMutation,
    disconnect: disconnectMutation,
    testConnection: testConnectionMutation,
    testClickpointConnection: testClickpointConnectionMutation,
    requestIntegration: requestIntegrationMutation,
    requestProfilePropertiesContact: requestProfilePropertiesContactMutation,
  }
}
